/**
 * ProviderOfficesPanel — the provider-office directory (the "Provider offices"
 * sub-tab of the Fax tab, formerly the standalone /crm/provider-offices page).
 *
 * The reusable book of doctor offices we fax records-release requests to:
 * office name, the doctors who practice there, ONE fax number, a location, free
 * notes (to keep tabs on how they perform over time), and a manual 1-5 rating.
 * Delivery stats (sent / delivered / failed, last faxed) are DERIVED server-side
 * from the fax history. The records-release fax modal autofills from here and
 * auto-saves new offices back, so this list builds itself as you fax.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Building2, Loader2, Plus, Pencil, Trash2, Star, Search, CheckCircle2, XCircle } from 'lucide-react';
import api from '../../../services/api';
import { ProviderOffice } from '../../types';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** +1NNNNNNNNNN -> (NNN) NNN-NNNN; pass through anything unexpected. */
function fmtFax(fax: string | null): string {
  if (!fax) return '';
  const d = fax.replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : fax;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const inputCls =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';
const lblCls = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

/** Read-only or interactive 1-5 stars. */
function Stars({ value, onChange }: { value: number | null; onChange?: (v: number | null) => void }) {
  return (
    <span className="inline-flex items-center">
      {[1, 2, 3, 4, 5].map((n) => {
        const filled = (value || 0) >= n;
        const star = (
          <Star className={`h-4 w-4 ${filled ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} />
        );
        return onChange ? (
          <button
            key={n}
            type="button"
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            onClick={() => onChange(value === n ? null : n)}
            className="p-0.5 transition-transform hover:scale-110"
          >
            {star}
          </button>
        ) : (
          <span key={n} aria-hidden="true">{star}</span>
        );
      })}
    </span>
  );
}

interface FormState {
  office_name: string;
  doctors: string; // comma-separated in the form
  fax_number: string;
  location: string;
  notes: string;
  rating: number | null;
}

const emptyForm: FormState = { office_name: '', doctors: '', fax_number: '', location: '', notes: '', rating: null };

function OfficeForm({ office, onClose, onSaved }: {
  office: ProviderOffice | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>(
    office
      ? {
          office_name: office.office_name,
          doctors: (office.doctor_names || []).join(', '),
          fax_number: fmtFax(office.fax_number),
          location: office.location || '',
          notes: office.notes || '',
          rating: office.rating,
        }
      : emptyForm,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof FormState, v: string | number | null) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.office_name.trim()) { setError('Office name is required.'); return; }
    setSaving(true);
    setError(null);
    const payload = {
      office_name: form.office_name.trim(),
      doctor_names: form.doctors.split(',').map((d) => d.trim()).filter(Boolean),
      fax_number: form.fax_number.trim(),
      location: form.location.trim(),
      notes: form.notes.trim(),
      rating: form.rating,
    };
    try {
      if (office) await api.patch(`/api/crm/provider-offices/${office.id}`, payload);
      else await api.post('/api/crm/provider-offices', payload);
      onSaved();
    } catch (e: any) {
      setError(errMsg(e));
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-xl bg-white p-5 shadow-xl dark:bg-gray-800 sm:rounded-xl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{office ? 'Edit office' : 'Add office'}</h3>
        <div className="mt-4 space-y-3">
          <div>
            <label className={lblCls}>Office name</label>
            <input className={inputCls} value={form.office_name} onChange={(e) => set('office_name', e.target.value)} placeholder="Wake Internal Medicine" autoFocus />
          </div>
          <div>
            <label className={lblCls}>Doctors <span className="font-normal">(comma-separated)</span></label>
            <input className={inputCls} value={form.doctors} onChange={(e) => set('doctors', e.target.value)} placeholder="Dr. Morgante, Dr. Lee" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={lblCls}>Fax number</label>
              <input type="tel" inputMode="tel" className={inputCls} value={form.fax_number} onChange={(e) => set('fax_number', e.target.value)} placeholder="(919) 555-0142" />
            </div>
            <div>
              <label className={lblCls}>Location</label>
              <input className={inputCls} value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="Raleigh, NC" />
            </div>
          </div>
          <div>
            <label className={lblCls}>Rating</label>
            <Stars value={form.rating} onChange={(v) => set('rating', v)} />
          </div>
          <div>
            <label className={lblCls}>Notes</label>
            <textarea className={`${inputCls} min-h-[80px]`} value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="How they respond, who to ask for, turnaround time…" />
          </div>
        </div>
        {error && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 min-h-[44px] sm:min-h-0">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProviderOfficesPanel() {
  const [offices, setOffices] = useState<ProviderOffice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderOffice | null>(null);
  const [deleting, setDeleting] = useState<ProviderOffice | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/crm/provider-offices');
      setOffices(res.data?.offices || []);
      setError(null);
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return offices;
    return offices.filter((o) =>
      o.office_name.toLowerCase().includes(q)
      || (o.doctor_names || []).some((d) => d.toLowerCase().includes(q))
      || (o.location || '').toLowerCase().includes(q)
      || (o.fax_number || '').includes(q));
  }, [offices, query]);

  const remove = async () => {
    if (!deleting) return;
    setBusy(true);
    try {
      await api.delete(`/api/crm/provider-offices/${deleting.id}`);
      setDeleting(null);
      await load();
    } catch (e: any) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Doctor offices we fax records requests to — fax numbers, ratings, notes, and delivery history.
        </p>
        <button
          type="button"
          onClick={() => { setEditing(null); setFormOpen(true); }}
          className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 sm:min-h-0"
        >
          <Plus className="h-4 w-4" /> Add office
        </button>
      </div>

      <div className="relative max-w-sm">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          className={`${inputCls} pl-9`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by office, doctor, location, or fax…"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300">{error}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-12 text-center dark:border-gray-600">
          <Building2 className="mx-auto h-8 w-8 text-gray-300 dark:text-gray-600" />
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {offices.length === 0
              ? 'No offices yet. Add one, or they’ll be saved automatically the next time you fax a release.'
              : 'No offices match your search.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {filtered.map((o) => (
            <div key={o.id} className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h3 className="truncate font-semibold text-gray-900 dark:text-gray-100">{o.office_name}</h3>
                  {o.doctor_names?.length > 0 && (
                    <p className="mt-0.5 truncate text-sm text-gray-600 dark:text-gray-400">{o.doctor_names.join(', ')}</p>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-1">
                  <button type="button" aria-label="Edit office" onClick={() => { setEditing(o); setFormOpen(true); }} className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700 dark:hover:text-gray-200">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button type="button" aria-label="Delete office" onClick={() => setDeleting(o)} className="rounded-lg p-2 text-gray-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                {o.fax_number && <span className="font-medium text-gray-700 dark:text-gray-300">{fmtFax(o.fax_number)}</span>}
                {o.location && <span className="text-gray-500 dark:text-gray-400">{o.location}</span>}
                {o.rating != null && <Stars value={o.rating} />}
              </div>

              {/* Derived delivery history */}
              <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                {o.stats.sent === 0 ? (
                  <span>No faxes sent yet</span>
                ) : (
                  <>
                    <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400">
                      <CheckCircle2 className="h-3.5 w-3.5" /> {o.stats.delivered} delivered
                    </span>
                    {o.stats.failed > 0 && (
                      <span className="inline-flex items-center gap-1 text-red-600 dark:text-red-400">
                        <XCircle className="h-3.5 w-3.5" /> {o.stats.failed} failed
                      </span>
                    )}
                    <span>{o.stats.sent} sent total</span>
                    {o.stats.last_faxed_at && <span>· last {fmtDate(o.stats.last_faxed_at)}</span>}
                  </>
                )}
              </div>

              {o.notes && (
                <p className="mt-2 whitespace-pre-wrap rounded-md bg-gray-50 p-2 text-xs text-gray-600 dark:bg-gray-900/40 dark:text-gray-300">{o.notes}</p>
              )}
            </div>
          ))}
        </div>
      )}

      {formOpen && (
        <OfficeForm
          office={editing}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); }}
        />
      )}

      {deleting && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={() => setDeleting(null)}>
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Delete this office?</h3>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              “{deleting.office_name}” will be removed from the directory. Past faxes are unaffected.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setDeleting(null)} className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700">Cancel</button>
              <button type="button" onClick={remove} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {busy && <Loader2 className="h-4 w-4 animate-spin" />} Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
